// Base on-demand component for tooltip / popover templates
//
//   Responsible for positioning and transitioning the template
//   Templates are only instantiated when shown, and destroyed when hidden
//

import Vue from '../../../lib/utils/vue'
import { createPopper } from '@popperjs/core'
import { getCS, select, getById } from '../../../lib/utils/dom'
import { HTMLElement } from '../../../lib/utils/safe-types'
import { BVTransition } from '../../../lib/utils/bv-transition'

const NAME = 'CvtPopper'

const AttachmentMap = {
  AUTO: 'auto',
  TOP: 'top',
  RIGHT: 'right',
  BOTTOM: 'bottom',
  LEFT: 'left',
  TOPLEFT: 'top',
  TOPRIGHT: 'top',
  RIGHTTOP: 'right',
  RIGHTBOTTOM: 'right',
  BOTTOMLEFT: 'bottom',
  BOTTOMRIGHT: 'bottom',
  LEFTTOP: 'left',
  LEFTBOTTOM: 'left'
}

const OffsetMap = {
  AUTO: 0,
  TOPLEFT: -1,
  TOP: 0,
  TOPRIGHT: +1,
  RIGHTTOP: -1,
  RIGHT: 0,
  RIGHTBOTTOM: +1,
  BOTTOMLEFT: -1,
  BOTTOM: 0,
  BOTTOMRIGHT: +1,
  LEFTTOP: -1,
  LEFT: 0,
  LEFTBOTTOM: +1
}

// @vue/component
export const CvtPopper = /*#__PURE__*/ Vue.extend({
  name: NAME,
  props: {
    target: {
      // Element that the tooltip/popover is positioned relative to
      type: [String, HTMLElement],
      default: null
    },
    placement: {
      type: String,
      default: 'top'
    },
    fallbackPlacement: {
      type: [String, Array],
      default: 'flip'
    },
    offset: {
      type: [Number, String],
      default: 0
    },
    boundary: {
      // 'scrollParent', 'viewport', 'window', or Element
      type: [String, HTMLElement],
      default: 'scrollParent'
    },
    boundaryPadding: {
      // Tooltip/popover will try and stay away from
      // boundary edge by this many pixels
      type: Number,
      default: 8
    },
    arrowPadding: {
      // The minimum distance (in `px`) from the edge of the
      // tooltip/popover that the arrow can be positioned
      type: Number,
      default: 5
    }
  },
  data() {
    return {
      // reactive props set by parent
      noFade: false,
      // State related data
      localShow: true,
      attachment: this.getAttachment(this.placement)
    }
  },
  computed: {
    templateType() /* istanbul ignore next */ {
      // Overridden by template component
      return 'unknown'
    },
    popperConfig() {
      const placement = this.placement
      return {
        placement: this.getAttachment(placement),
        padding: this.boundaryPadding,
        modifiers: [
          {
            name: 'flip',
            options: {
              padding: this.boundaryPadding,
              fallbackPlacements: [this.fallbackPlacement, 'left', 'right'],
              flipVariations: false,
              boundary: this.boundary
            }
          },
          {
            name: 'preventOverflow',
            options: {
              padding: this.boundaryPadding,
              boundary: this.boundary
            }
          },
          {
            name: 'arrow',
            options: {
              element: '.arrowtooltip',
              padding: this.arrowPadding
            }
          },
          {
            name: 'offset',
            options: {
              offset: [0, this.getOffset(placement)]
            }
          }
        ],

        onCreate: (data) => {
          // Handle flipping arrow classes
          if (data.originalPlacement !== data.placement) {
            /* istanbul ignore next: can't test in JSDOM */
            this.popperPlacementChange(data)
          }
        },
        onUpdate: (data) => {
          // Handle flipping arrow classes
          this.popperPlacementChange(data)
        }
      }
    }
  },
  created() {
    // Note: We are created on-demand, and should be guaranteed that
    // DOM is rendered/ready by the time the created hook runs
    this.$_popper = null
    // Ensure we show as we mount
    this.localShow = true
    // Create popper instance before shown
    this.$on('show', (el) => {
      this.popperCreate(el)
    })
    // Self destruct once hidden
    this.$on('hidden', () => {
      this.$nextTick(this.$destroy)
    })
  },
  beforeMount() {
    // Ensure that the attachment position is correct before mounting
    // as our propsData is added after `new Template({...})`
    this.attachment = this.getAttachment(this.placement)
  },
  mounted() {
    this.popperCreate(this.$el)
  },
  updated() {
    // Update popper if needed
    this.popperUpdate()
  },
  beforeDestroy() {
    this.popperDestroy()
  },
  destroyed() {
    // Make sure template is removed from DOM
    const el = this.$el
    el && el.parentNode && el.parentNode.removeChild(el)
  },
  methods: {
    // "Public" method to trigger hide template
    hide() {
      this.localShow = false
    },
    // Private
    getAttachment(placement) {
      return AttachmentMap[String(placement).toUpperCase()] || 'auto'
    },
    getOffset(placement) {
      if (!this.offset) {
        // Could set a ref for the arrow element
        console.log(this.$refs.arrow)
        const arrow = this.$refs.arrow || select('.arrowtooltip', this.$el)
        const arrowOffset =
          (parseFloat(getCS(arrow).width) || 0) +
          (parseFloat(this.arrowPadding) || 0)
        switch (OffsetMap[String(placement).toUpperCase()] || 0) {
          case +1:
            /* istanbul ignore next: can't test in JSDOM */
            return `+50%p - ${arrowOffset}px`
          case -1:
            /* istanbul ignore next: can't test in JSDOM */
            return `-50%p + ${arrowOffset}px`
          default:
            return 0
        }
      }
      /* istanbul ignore next */
      return this.offset
    },
    popperCreate(el) {
      this.popperDestroy()
      // We use `el` rather than `this.$el` just in case the original
      // mountpoint root element type was changed by the template
      const target =
        typeof this.target === 'string' ? select(this.target) : this.target
      this.$_popper = new createPopper(target, el, this.popperConfig)
    },
    popperDestroy() {
      this.$_popper && this.$_popper.destroy()
      this.$_popper = null
    },
    popperUpdate(options) {
      options && this.$_popper && this.$_popper.setOptions(options)
      this.$_popper && this.$_popper.update()
    },
    popperPlacementChange(data) {
      // Callback used by popper to adjust the arrow placement
      this.attachment = this.getAttachment(data.placement)
    },
    renderTemplate(h) /* istanbul ignore next */ {
      // Will be overridden by templates
      return h('div', {}, this.$slots.default)
    }
  },
  render(h) {
    // `show` and 'fade' classes are only applied during transition
    return (
      <BVTransition
        appear={true}
        noFade={this.noFade}
        onBeforeEnter={(el) => this.$emit('show', el)}
        onAfterEnter={(el) => this.$emit('show', this.$el)}
        onBeforeLeave={(el) => this.$emit('hide', el)}
        onAfterLeave={(el) => this.$emit('hidden', el)}
      >
        {this.localShow ? this.renderTemplate(h) : h()}
      </BVTransition>
    )
  }
})
